import React, { useEffect, useMemo, useRef, useState } from "react";
import "./UserDetailsModal.styles.scss";

// Component Imports
import {
  ActiveStateDisplayInput,
  DateDisplayInput,
  DefaultTextInput,
  DropdownInput,
} from "../../../Inputs";
import IconButtonCell from "@/components/AGCellRenderers/IconButtonCell";
import AGTable from "@/components/AGTable/AGTable";

// MUI Imports
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";

// Type Imports
import { IUser } from "@pulse-ui/types/components/RoleManagement";
import { IPortfolioMetadata } from "@pulse-ui/codegen/generated_ts/org_metadata";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { IAGPortfolioDetailsDataRow } from "@pulse-ui/types/components/AGTable";
import { ColDef } from "ag-grid-community";

// Lib Imports
import { formatIsoDateToLocale } from "@pulse-ui/formatting";

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  className: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function CustomDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#ebe93e",
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const UserDetailsModal = ({
  open,
  userDetails,
  portfolios_context_data,
  closeUserDetailsModal,
  copyToClipboard,
  setDeleteModalStatus,
  handleRoleChange,
  handleUpdateUserPortfolios,
}: {
  open: boolean;
  userDetails: IUser | null;
  portfolios_context_data: IPortfolioMetadata[];
  closeUserDetailsModal: () => void;
  copyToClipboard: (text: string, name: string) => Promise<void>;
  setDeleteModalStatus: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      item_id: string | null;
      item_name: string | null;
      delete_type: "user" | "portfolio" | null;
      org_user_id?: string | null;
    }>
  >;
  handleRoleChange: (org_user_id: string, roles: string[]) => Promise<void>;
  handleUpdateUserPortfolios: (
    org_user_id: string,
    portfolios: string[]
  ) => Promise<void>;
}) => {
  // States - START ------------------------------------
  const inputFields: {
    name: string;
    label: string;
    type: string;
  }[] = [
    {
      name: "org_user_id",
      label: "User ID",
      type: "text",
    },
    {
      name: "is_active",
      label: "Active",
      type: "boolean",
    },
    {
      name: "firstname",
      label: "First Name",
      type: "text",
    },
    {
      name: "lastname",
      label: "Last Name",
      type: "text",
    },
    {
      name: "email",
      label: "Email Address",
      type: "text",
    },
    {
      name: "created",
      label: "Created",
      type: "date",
    },
    {
      name: "last_modified",
      label: "Last Modified",
      type: "date",
    },
  ];

  // AG GRID -----------------------------
  const gridRef = useRef();
  const [rowData, setRowData] = useState<IAGPortfolioDetailsDataRow[]>([]);

  const [userPortfolioDetails, setUserPortfolioDetails] = useState<
    IPortfolioMetadata[] | null
  >(null);
  const portfolioRoles = ["admin", "trader", "viewer"];
  const containsRoles =
    userDetails?.roles &&
    userDetails.roles.some((r) => portfolioRoles.includes(r));
  const [portfolioDropdownValue, setPortfolioDropdownValue] =
    useState<string>("");
  const [unassignedPortfolioOptions, setUnassignedPortfolioOptions] = useState<
    ISelectOption[]
  >([]);
  // States - END --------------------------------------

  // Methods - END -------------------------------------
  const parse_portfolio_to_row_data = (
    portfolios: IPortfolioMetadata[]
  ): IAGPortfolioDetailsDataRow[] => {
    return portfolios.map((portfolio: IPortfolioMetadata) => {
      return {
        row_id: portfolio.portfolio_id,
        display_name: portfolio.display_name,
        portfolio_id: portfolio.portfolio_id,
        created: formatIsoDateToLocale(portfolio.created),
        last_modified: formatIsoDateToLocale(portfolio.last_modified || ""),
        delete: portfolio.portfolio_id,
      };
    });
  };

  const handleRoleSwitchClick = async (
    e: React.SyntheticEvent<Element, Event>
  ) => {
    if (userDetails) {
      const target = e.target as HTMLInputElement;
      await handleRoleChange(
        userDetails.org_user_id,
        target.value === "" ? [] : [target.value]
      );
    }
  };

  const handleAssignPortfolio = (new_portfolio_id: string) => {
    if (userDetails) {
      const temp_new_portfolios_arr = [
        ...userDetails.portfolios,
        new_portfolio_id,
      ];

      handleUpdateUserPortfolios(
        userDetails.org_user_id,
        temp_new_portfolios_arr
      );
    }
  };

  // Column Definitions: Defines & controls grid columns.
  const colDefs = useMemo<ColDef<IAGPortfolioDetailsDataRow>[]>(
    () => [
      {
        headerName: "Name",
        field: "display_name",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "ID",
        field: "portfolio_id",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Created",
        field: "created",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Last Modified",
        field: "last_modified",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Delete",
        field: "delete",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: IconButtonCell,
        cellRendererParams: (params: any) => ({
          icon: DeleteIcon,
          hoverColorClass: "pulse-hover-color--error-700",
          onClick: () => {
            setDeleteModalStatus({
              open: true,
              item_id: params.value,
              item_name: params.data.display_name,
              delete_type: "portfolio",
              org_user_id: userDetails?.org_user_id || "",
            });
          },
        }),
        width: 100,
      },
    ],
    []
  );

  const defaultColDef: ColDef = {
    flex: 1,
  };
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    if (userDetails) {
      const user_portfolios_details: IPortfolioMetadata[] =
        userDetails?.portfolios
          .map((portfolio: string) => {
            return portfolios_context_data.find(
              (portfolio_metadata) =>
                portfolio_metadata.portfolio_id === portfolio
            );
          })
          .filter(
            (portfolio: any): portfolio is IPortfolioMetadata =>
              portfolio !== undefined
          ) || [];

      let unassigned_portfolios_details: ISelectOption[] = [];

      portfolios_context_data.forEach((portfolio_metadata) => {
        if (!userDetails.portfolios.includes(portfolio_metadata.portfolio_id)) {
          unassigned_portfolios_details.push({
            label: portfolio_metadata.display_name,
            value: portfolio_metadata.portfolio_id,
          });
        }
      });

      setUserPortfolioDetails(user_portfolios_details);
      setUnassignedPortfolioOptions(unassigned_portfolios_details);
    }
  }, [userDetails]);

  useEffect(() => {
    if (userPortfolioDetails) {
      const parsed_data_rows: IAGPortfolioDetailsDataRow[] =
        parse_portfolio_to_row_data(userPortfolioDetails);
      setRowData(parsed_data_rows);
    }
  }, [userPortfolioDetails]);
  // useEffects - END ----------------------------------

  return (
    inputFields && (
      <div className="user-details-modal">
        <CustomDialog
          onClose={closeUserDetailsModal}
          open={open}
          className="user-details-modal-dialog"
          maxWidth="xl"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              boxShadow: "0px 0px 4px #777",
            },
          }}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
            },
          }}
        >
          <CustomDialogTitle
            className="user-details-modal-dialog-title"
            onClose={closeUserDetailsModal}
          >
            {userDetails && userDetails.firstname && userDetails.lastname
              ? `${userDetails.firstname} ${userDetails.lastname}`
              : "User Details"}
          </CustomDialogTitle>
          <DialogContent dividers className="user-details-modal-dialog-content">
            {userDetails && userDetails.roles && (
              <div
                className="pulse-row"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <DropdownInput
                  name="roles"
                  value={
                    userDetails.roles.includes("admin")
                      ? "admin"
                      : userDetails.roles.includes("account_manager")
                      ? "account_manager"
                      : userDetails.roles.includes("sales")
                      ? "sales"
                      : userDetails.roles.includes("risk")
                      ? "risk"
                      : userDetails.roles.includes("trader")
                      ? "trader"
                      : userDetails.roles.includes("viewer")
                      ? "viewer"
                      : ""
                  }
                  label="Role"
                  containerClassNames="pulse-color--background-50"
                  options={[
                    { label: "Viewer", value: "viewer" },
                    { label: "Trader", value: "trader" },
                    { label: "Risk", value: "risk" },
                    { label: "Sales", value: "sales" },
                    { label: "Account Manager", value: "account_manager" },
                    { label: "Admin", value: "admin" },
                  ]}
                  onChange={handleRoleSwitchClick}
                  disabled={false}
                  formInput={false}
                />
              </div>
            )}

            <div className="pulse-row pulse-gap-1 pulse-p-1">
              {inputFields.map((field: any) =>
                field.type === "text" ? (
                  <DefaultTextInput
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    value={userDetails && (userDetails as any)?.[field.name]}
                    onChange={() => {}}
                    copyIcon={<ContentCopyIcon />}
                    copyToClipboard={copyToClipboard}
                    containerClassNames="pulse-default-text-input pulse-br-sm pulse-mb-2 pulse-col-xs-6 pulse-col-sm-6 pulse-col-md-6 pulse-col-lg-6 pulse-col-xl-6"
                    labelClassNames={`pulse-color--background-50
                pulse-typography-body-2 pulse-mb-1`}
                    inputClassNames="pulse-bg-color--background-800 pulse-typography-body-2"
                    type="text"
                    disabled
                  />
                ) : field.type === "boolean" ? (
                  <ActiveStateDisplayInput
                    key={field.name}
                    value={userDetails?.is_active || false}
                  />
                ) : field.type === "date" ? (
                  <DateDisplayInput
                    key={field.name}
                    name={field.name}
                    value={userDetails && (userDetails as any)?.[field.name]}
                    label={field.label}
                  />
                ) : null
              )}
            </div>

            <p className="pulse-color--background-50">
              {containsRoles
                ? rowData && rowData.length > 0
                  ? "Assigned Portfolios"
                  : "No Assigned Portfolios"
                : "Please select a role for the user before assigning portfolios"}
            </p>

            {containsRoles && (
              <div
                className="pulse-row pulse-gap-1 pulse-p-1"
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <DropdownInput
                  name="Portfolio"
                  value={portfolioDropdownValue}
                  label="New Portfolio"
                  onChange={(e) => setPortfolioDropdownValue(e.target.value)}
                  disabled={false}
                  options={unassignedPortfolioOptions}
                  formInput
                  containerClassNames="pulse-mr-2 pulse-p-0"
                />

                <Button
                  sx={{
                    backgroundColor: "#0c1835",
                    color: "#ebe93e",
                    "&.Mui-disabled": {
                      color: "#939db6",
                    },
                  }}
                  variant="text"
                  size="medium"
                  onClick={() => {
                    handleAssignPortfolio(portfolioDropdownValue);
                    setPortfolioDropdownValue("");
                  }}
                  disabled={
                    !portfolioDropdownValue || portfolioDropdownValue.length < 1
                  }
                >
                  Assign Portfolio
                </Button>
              </div>
            )}

            {containsRoles && rowData && rowData.length > 0 && (
              <AGTable
                gridRef={gridRef}
                rowData={rowData}
                colDefs={colDefs}
                defaultColDef={defaultColDef}
                groupDisplayType="singleColumn"
                sideBar={false}
                animateRows
                tableHeight={300}
              />
            )}
          </DialogContent>
          <DialogActions className="user-details-modal-dialog-actions"></DialogActions>
        </CustomDialog>
      </div>
    )
  );
};
export default UserDetailsModal;
